/**
 * The clinic dashboard, rebuilt to Brian's 23 September mockup.
 *
 * His brief: "Completely redo the Clinic Dashboard using the new MCOS
 * aesthetic/mockups I sent you." The mockup is two columns under the header:
 * the New Patients work queue on the left (lab results ready, treatment plan
 * needed) and the selected patient's panel on the right, where the doctor
 * builds and sends the plan without leaving the page.
 *
 * Nofal's brief on the same call: the doctor should not have to "navigate all
 * over and check either new patient arrived or not", and the whole page
 * updates live over the socket: a new patient, a critical lab or a RETAIN
 * review appears without a reload.
 *
 * The top-right card slot of the panel, the place the mockup gave the Overall
 * Score that Brian removed, first held the clinic's Physician Alerts. On 26
 * September Brian moved those into a labelled box in the header ("replacing
 * the bell icon with the dot with a clearly labelled Physician Alerts box")
 * and asked for "something useful there", so the slot now shows the selected
 * patient's status: programme month, next blood draw, last RETAIN check-in
 * and their own open lab alerts.
 *
 * Above the two columns, four clinic figures in the RETAIN mockup's own KPI
 * card (Nofal: "we can keep such KPI's on dashboard, like follow exact shared
 * reference theme"). The old fourth tile, the average wellness score, was the
 * overall score Brian removed, so that place shows the patients waiting for a
 * plan instead.
 *
 * Gone, per the brief: the upload action, the awaiting-review banner, Top
 * Priorities and All Patients tabs, the Wellness Score Overview and every
 * overall score.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ProtocolNotices } from '../components/ProtocolNotices';
import { Avatar, ErrorState, LoadingRows } from '../components/ui';
import {
  IconAlert,
  IconCheck,
  IconChevron,
  IconClipboard,
  IconHeart,
  IconReports,
  IconUsers,
} from '../components/icons';
import { useShellTitle } from '../components/Shell';
import { PatientStatusCard, PatientWorkspace } from '../components/PatientWorkspace';

interface QueueEntry {
  patientId: string;
  firstName: string;
  lastName: string;
  primaryGoal: string | null;
  goalHeadline: string | null;
  labsReadyDaysAgo: number;
  isNew: boolean;
}

export function DashboardPage() {
  const navigate = useNavigate();
  useShellTitle('Clinic Dashboard', 'Manage your patients. Personalize treatment. Better outcomes.');
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const queue = useQuery({
    queryKey: ['treatment-queue'],
    queryFn: () => api.get<{ queue: QueueEntry[] }>('/regimens/queue'),
  });

  const entries = queue.data?.queue ?? [];
  const requested = params.get('patient');
  const selectedId = requested ?? entries[0]?.patientId ?? null;
  const selectedEntry = entries.find((entry) => entry.patientId === selectedId) ?? null;

  /* Opening a new patient clears their "New" marker for the whole clinic,
     and the matching bell item with it. */
  const seen = useMutation({
    mutationFn: (patientId: string) => api.post(`/patients/${patientId}/seen`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['treatment-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['notification-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const lastSeen = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedEntry?.isNew || lastSeen.current === selectedEntry.patientId) return;
    lastSeen.current = selectedEntry.patientId;
    seen.mutate(selectedEntry.patientId);
  }, [selectedEntry, seen]);

  /* The panel moves on to the next patient once a plan is sent, so the
     confirmation has to live here, above both columns. */
  const [flash, setFlash] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 10_000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const select = (patientId: string) => {
    const copy = new URLSearchParams(params);
    copy.set('patient', patientId);
    copy.delete('tab');
    setParams(copy, { replace: true });
  };

  return (
    <div className="lg:-mx-4 lg:-mt-4">
    <DashboardKpis waiting={queue.data ? entries.length : null} />
    <ProtocolNotices onRevise={(patientId) => navigate(`/patients/${patientId}/workspace`)} />
    {flash && (
      <div
        role="status"
        className={`mb-[13px] flex items-center gap-3 rounded-xl border px-4 py-3 text-[14px] ${
          flash.tone === 'ok'
            ? 'border-[#bcd5c9] bg-mint text-forest'
            : 'border-[#ecd9a0] bg-risk-yellow-soft text-risk-yellow'
        }`}
      >
        {flash.tone === 'ok' ? <IconCheck size={18} /> : <IconAlert size={18} />}
        <span className="flex-1">{flash.text}</span>
        <button type="button" onClick={() => setFlash(null)} className="text-[13px] font-semibold underline-offset-2 hover:underline">
          Dismiss
        </button>
      </div>
    )}
    <div className="grid grid-cols-1 items-start gap-[13px] lg:grid-cols-[312px_minmax(0,1fr)]">
      <NewPatients
        entries={entries}
        loading={queue.isLoading}
        error={queue.error}
        retry={() => void queue.refetch()}
        selectedId={selectedId}
        onSelect={select}
      />

      <div className="min-w-0">
        {selectedId ? (
          <PatientWorkspace
            key={selectedId}
            patientId={selectedId}
            backTo="/patients"
            headerCard={(data, openTab) => <PatientStatusCard data={data} onOpenLabs={() => openTab('labs')} />}
            onPlanSent={(outcome) => {
              setFlash(outcome);
              // The patient has left the queue; the next one comes up.
              const copy = new URLSearchParams(params);
              copy.delete('patient');
              copy.delete('tab');
              setParams(copy, { replace: true });
            }}
          />
        ) : (
          <section className="rounded-[14px] border border-hairline bg-[#fdfdfb] p-4">
            <div className="max-w-md pt-2">
                <p className="font-display text-[22px] text-navy" style={{ fontWeight: 560 }}>
                  {queue.isLoading ? 'Loading patients' : 'No patient waiting for a plan'}
                </p>
                <p className="mt-1 text-[14.5px] text-[#4d6072]">
                  When a patient's labs are scored and their report is ready, they appear on the left for a treatment
                  decision, and here with their MCOS treatment plan.
                </p>
            </div>
          </section>
        )}
      </div>
    </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Clinic figures, in the RETAIN mockup's KPI card
 * ------------------------------------------------------------------ */

interface DashboardStats {
  stats: {
    totalPatients: { value: number; deltaThisMonth: number };
    reportsGenerated: { value: number; deltaThisMonth: number };
    activePatients: { value: number; windowDays: number };
  };
}

function Kpi({
  icon,
  label,
  value,
  caption,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | null;
  caption: string;
}) {
  const shown = useCountUp(value);
  return (
    <div className="mcos-card flex flex-col items-start gap-3 rounded-2xl border border-hairline bg-white px-4 py-4 sm:flex-row sm:items-center sm:gap-4">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-mint text-forest sm:h-14 sm:w-14">{icon}</span>
      <span className="min-w-0 max-w-full">
        <span title={label} className="block break-words text-[12px] font-semibold uppercase leading-snug tracking-[0.05em] text-navy sm:text-[12.5px] sm:leading-normal sm:tracking-[0.06em]">{label}</span>
        <span className="mt-1 block font-display text-[32px] leading-none text-navy">
          {value === null ? <span className="inline-block h-7 w-10 animate-pulse rounded bg-ink/5 align-middle" /> : shown}
        </span>
        <span title={caption} className="break-words mt-1.5 block text-[13.5px] leading-snug text-[#4d6072] sm:leading-normal">{caption}</span>
      </span>
    </div>
  );
}

/**
 * A figure that counts up to its value the first time it arrives, then
 * follows later changes directly. Skipped for reduced-motion users.
 */
function useCountUp(value: number | null): number | null {
  const [shown, setShown] = useState<number | null>(value);
  const started = useRef(false);
  useEffect(() => {
    if (value === null) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (started.current || reduce || value === 0) {
      setShown(value);
      return;
    }
    started.current = true;
    const duration = 700;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setShown(Math.round(value * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return shown;
}

/** Whole-clinic figures, never the list below them. */
function DashboardKpis({ waiting }: { waiting: number | null }) {
  const stats = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get<DashboardStats>('/dashboard'),
  });
  const data = stats.data?.stats;
  const month = (delta: number | undefined) => (delta === undefined ? ' ' : `+${delta} this month`);
  return (
    <div className="mcos-stagger mb-[13px] grid grid-cols-2 gap-[13px] xl:grid-cols-4">
      <Kpi icon={<IconUsers size={26} />} label="Total Patients" value={data?.totalPatients.value ?? null} caption={month(data?.totalPatients.deltaThisMonth)} />
      <Kpi icon={<IconClipboard size={26} />} label="Awaiting Treatment Plan" value={waiting} caption="labs ready, plan needed" />
      <Kpi icon={<IconReports size={26} />} label="Reports Generated" value={data?.reportsGenerated.value ?? null} caption={month(data?.reportsGenerated.deltaThisMonth)} />
      <Kpi
        icon={<IconHeart size={26} />}
        label="Active Patients"
        value={data?.activePatients.value ?? null}
        caption={data ? `seen in ${data.activePatients.windowDays} days` : ' '}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * New Patients, the left column
 * ------------------------------------------------------------------ */

function daysAgo(days: number): string {
  if (days <= 0) return 'today';
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

function NewPatients({
  entries,
  loading,
  error,
  retry,
  selectedId,
  onSelect,
}: {
  entries: QueueEntry[];
  loading: boolean;
  error: unknown;
  retry: () => void;
  selectedId: string | null;
  onSelect: (patientId: string) => void;
}) {
  return (
    <section className="flex flex-col overflow-hidden rounded-[12px] border border-hairline bg-[#fdfdfb] lg:sticky lg:top-0 lg:max-h-[calc(100vh/var(--app-zoom)-112px)]">
      <div className="px-[18px] pt-4 pb-2.5">
        <h2 className="font-display text-[22px] leading-tight text-navy" style={{ fontWeight: 560 }}>
          New Patients ({entries.length})
        </h2>
        <p className="mt-1 text-[14px] text-[#5b6b7c] sm:whitespace-nowrap">
          Lab results ready <span aria-hidden="true" className="mx-0.5 text-[11px]">•</span> Treatment plan needed
        </p>
      </div>

      {error ? (
        <div className="p-4"><ErrorState error={error} retry={retry} /></div>
      ) : loading ? (
        <div className="p-4"><LoadingRows rows={6} /></div>
      ) : entries.length === 0 ? (
        <p className="px-[18px] pb-6 pt-2 text-[14px] text-[#5b6b7c]">
          Nobody is waiting. A patient appears here once their report is ready and no plan has been sent.
        </p>
      ) : (
        <ul className="mcos-stagger no-scrollbar flex-1 overflow-y-auto pb-2">
          {entries.map((entry, index) => {
            const selected = entry.patientId === selectedId;
            return (
              <li key={entry.patientId}>
                {index > 0 && !selected && entries[index - 1]?.patientId !== selectedId && (
                  <div aria-hidden="true" className="mx-[14px] h-px bg-hairline/70" />
                )}
                <button
                  type="button"
                  onClick={() => onSelect(entry.patientId)}
                  aria-current={selected ? 'true' : undefined}
                  className={`relative flex w-full items-center gap-[14px] py-[18px] pl-[10px] pr-[16px] text-left transition-colors ${
                    selected ? 'bg-[#e5f1e8]' : 'hover:bg-paper'
                  }`}
                >
                  {selected && <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-forest" />}
                  <Avatar firstName={entry.firstName} lastName={entry.lastName} size="lg" className="!bg-[#e9eeec]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-display text-[18px] leading-tight text-navy" style={{ fontWeight: 560 }}>
                      {entry.firstName} {entry.lastName}
                    </span>
                    <span className={`mt-1 block truncate text-[15px] ${selected ? 'text-alert' : 'text-[#566577]'}`}>
                      Goal: {entry.goalHeadline ?? 'not recorded'}
                    </span>
                    <span className="mt-1 block text-[14px] text-[#7a8591]">
                      Labs ready <span aria-hidden="true" className="mx-0.5 text-[11px]">•</span> {daysAgo(entry.labsReadyDaysAgo)}
                    </span>
                  </span>
                  {entry.isNew && (
                    <span className="absolute right-[16px] top-[15px] rounded-full bg-alert px-2.5 py-[3px] text-[12.5px] leading-none text-white">
                      New
                    </span>
                  )}
                  <IconChevron size={20} className="mt-5 shrink-0 text-navy" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
